import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { api } from "../../lib/api";
import { DialogPanel } from "../shared/DialogPanel";
import { PrinterIcon, AlertTriangleIcon } from "../shared/Icons";
import { backdropClose } from "../shared/backdrop";

interface GlobalVar {
  id: string;
  key: string;
  kind: "static" | "template" | "webhook_path" | "python";
  value: string;
  sample_payload: any;
  description: string | null;
}

function apiErr(e: any): string {
  const d = e?.payload?.detail;
  if (Array.isArray(d)) return d.map((x: any) => x?.msg ?? JSON.stringify(x)).join("; ");
  if (typeof d === "string") return d;
  return e?.message ?? "Erro";
}

const inp: React.CSSProperties = {
  padding: "8px 12px",
  background: "var(--surface-input)",
  border: "1px solid var(--line-strong)",
  borderRadius: 8,
  color: "var(--ink)",
  fontSize: 13,
  outline: "none",
  width: "100%",
  boxSizing: "border-box",
};

export function VariablesTab() {
  const qc = useQueryClient();
  const { data: vars = [] } = useQuery<GlobalVar[]>({
    queryKey: ["dev-vars"],
    queryFn: () => api.get("/dev/variables"),
  });
  const { data: catalog = [] } = useQuery<{ label: string; category: string; description: string; insert: string }[]>({
    queryKey: ["dev-expr-catalog"],
    queryFn: () => api.get("/dev/expressions/catalog"),
  });
  const userKeys = new Set(vars.map((v) => v.key));
  const system = catalog.filter((c) => !userKeys.has(c.label));
  const [editing, setEditing] = useState<Partial<GlobalVar> | null>(null);
  const [confirmDel, setConfirmDel] = useState<GlobalVar | null>(null);

  const del = useMutation({
    mutationFn: (id: string) => api.delete(`/dev/variables/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["dev-vars"] }),
  });

  return (
    <div>
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
        <span style={{ fontSize: 14, fontWeight: 600, color: "var(--ink)" }}>Variáveis Globais</span>
        <button
          onClick={() => setEditing({ kind: "static", key: "", value: "" })}
          style={{ padding: "7px 14px", background: "var(--brand)", border: "none", borderRadius: 8, color: "#fff", fontSize: 13, cursor: "pointer" }}
        >
          + Nova variável
        </button>
      </div>

      {/* Table */}
      {vars.length === 0 ? (
        <div style={{ color: "var(--ink-muted)", fontSize: 13, textAlign: "center", padding: "40px 0" }}>Nenhuma variável cadastrada</div>
      ) : (
        <div style={{ background: "var(--surface-hover)", border: "1px solid var(--line)", borderRadius: 10, overflow: "hidden" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: "1px solid var(--line)" }}>
                {["Chave", "Tipo", "Descrição", ""].map((h) => (
                  <th key={h} style={{ padding: "10px 14px", textAlign: "left", color: "var(--ink-muted)", fontWeight: 500, fontSize: 11, textTransform: "uppercase" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {vars.map((v) => (
                <tr key={v.id} style={{ borderBottom: "1px solid var(--surface-input)" }}>
                  <td style={{ padding: "10px 14px", fontFamily: "var(--font-mono)", color: "var(--brand)" }}>{v.key}</td>
                  <td style={{ padding: "10px 14px" }}>
                    <span style={{ fontSize: 11, padding: "2px 7px", borderRadius: 4, background: "var(--surface-input)", border: "1px solid var(--line-strong)", color: "var(--ink-secondary)" }}>{v.kind}</span>
                  </td>
                  <td style={{ padding: "10px 14px", color: v.description ? "var(--ink-secondary)" : "var(--ink-muted)", maxWidth: 320 }}>
                    <span style={{ display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={v.description ?? undefined}>{v.description || "—"}</span>
                  </td>
                  <td style={{ padding: "10px 14px", whiteSpace: "nowrap" }}>
                    <button onClick={() => setEditing(v)} style={{ background: "none", border: "none", color: "var(--ink-muted)", fontSize: 12, cursor: "pointer", marginRight: 8 }}>Editar</button>
                    <button onClick={() => setEditing({ kind: v.kind, key: `${v.key}_copia`, value: v.value, sample_payload: v.sample_payload, description: v.description })} style={{ background: "none", border: "none", color: "var(--ink-muted)", fontSize: 12, cursor: "pointer", marginRight: 8 }}>Duplicar</button>
                    <button onClick={() => setConfirmDel(v)} style={{ background: "none", border: "none", color: "var(--danger)", fontSize: 12, cursor: "pointer" }}>Excluir</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Variáveis do sistema: mesma fonte do "Inserir variável" (só leitura) */}
      {system.length > 0 && (
        <div style={{ marginTop: 28 }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: "var(--ink)", marginBottom: 4 }}>Variáveis do sistema</div>
          <div style={{ fontSize: 12, color: "var(--ink-muted)", marginBottom: 12 }}>Já existem em todo prompt/tool — não precisam ser cadastradas.</div>
          <div style={{ background: "var(--surface-hover)", border: "1px solid var(--line)", borderRadius: 10, overflow: "hidden" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <tbody>
                {system.map((c) => (
                  <tr key={`${c.category}:${c.label}`} style={{ borderTop: "1px solid var(--line)" }}>
                    <td style={{ padding: "8px 14px", fontFamily: "var(--font-mono)", whiteSpace: "nowrap" }}>{c.insert}</td>
                    <td style={{ padding: "8px 14px", color: "var(--ink-muted)", textTransform: "uppercase", fontSize: 10, whiteSpace: "nowrap" }}>{c.category}</td>
                    <td style={{ padding: "8px 14px", color: "var(--ink-secondary)" }}>{c.description}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {editing && (
        <VarModal
          initial={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { qc.invalidateQueries({ queryKey: ["dev-vars"] }); setEditing(null); }}
        />
      )}

      {confirmDel && (
        <div
          {...backdropClose(() => { setConfirmDel(null); })}
          style={{ position: "fixed", inset: 0, background: "var(--overlay)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: "var(--z-modal)" as any }}
        >
          <DialogPanel onClose={() => setConfirmDel(null)} role="alertdialog" label="Excluir variável" style={{ background: "var(--surface-app)", border: "1px solid var(--line)", borderRadius: 12, padding: 24, width: 420, display: "flex", flexDirection: "column", gap: 16 }}>
            <span style={{ fontSize: 15, fontWeight: 600, color: "var(--ink)" }}>Excluir variável</span>
            <span style={{ fontSize: 13, color: "var(--ink-secondary)", lineHeight: 1.5 }}>
              Excluir a variável <code style={{ color: "var(--brand)", fontFamily: "var(--font-mono)" }}>{confirmDel.key}</code>? Esta ação não pode ser desfeita.
            </span>
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button onClick={() => setConfirmDel(null)} style={{ padding: "8px 16px", background: "var(--surface-input)", border: "1px solid var(--line-strong)", borderRadius: 8, color: "var(--ink-secondary)", fontSize: 13, cursor: "pointer" }}>Cancelar</button>
              <button onClick={() => { del.mutate(confirmDel.id); setConfirmDel(null); }} style={{ padding: "8px 16px", background: "var(--brand)", border: "none", borderRadius: 8, color: "#fff", fontSize: 13, cursor: "pointer" }}>Excluir</button>
            </div>
          </DialogPanel>
        </div>
      )}
    </div>
  );
}

function VarModal({ initial, onClose, onSaved }: { initial: Partial<GlobalVar>; onClose: () => void; onSaved: () => void }) {
  const isEdit = !!initial.id;
  const [form, setForm] = useState({
    key: initial.key ?? "",
    kind: (initial.kind ?? "static") as "static" | "template" | "webhook_path" | "python",
    value: initial.value ?? "",
    description: initial.description ?? "",
  });
  const [sampleText, setSampleText] = useState(initial.sample_payload ? JSON.stringify(initial.sample_payload, null, 2) : "");
  const [error, setError] = useState("");
  const [testOut, setTestOut] = useState<{ output?: string; error?: string; result?: string } | null>(null);

  const test = useMutation({
    mutationFn: async () => {
      let sample: any = undefined;
      if (sampleText.trim()) {
        try { sample = JSON.parse(sampleText); } catch { throw new Error("Payload de exemplo: JSON inválido"); }
      }
      return api.post<{ ok: boolean; result: string; output: string; error: string }>(
        `/dev/expressions/preview`,
        { kind: form.kind, value: form.value, sample_payload: sample },
      );
    },
    onSuccess: (r) => {
      setTestOut({
        output: r.output && r.output.trim() ? r.output.replace(/\n+$/, "") : undefined,
        ...(r.error ? { error: r.error } : { result: r.result }),
      });
    },
    onError: (e: any) => setTestOut({ error: apiErr(e) }),
  });

  const mut = useMutation({
    mutationFn: () => {
      const body: any = { ...form };
      if (sampleText.trim()) {
        try { body.sample_payload = JSON.parse(sampleText); }
        catch { throw new Error("Payload de exemplo: JSON inválido"); }
      }
      if (isEdit) return api.patch(`/dev/variables/${initial.id}`, body);
      return api.post("/dev/variables", body);
    },
    onSuccess: onSaved,
    onError: (e: any) => setError(apiErr(e)),
  });

  const kindLabel: Record<string, string> = {
    static: "Estática",
    template: "Template Jinja",
    webhook_path: "Webhook (JMESPath)",
    python: "Python",
  };

  return (
    <div {...backdropClose(() => { onClose(); })} style={{ position: "fixed", inset: 0, background: "var(--overlay)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: "var(--z-modal)" as any }}>
      <DialogPanel onClose={() => onClose()} label={isEdit ? `Editar: ${initial.key}` : "Nova variável"} style={{ background: "var(--surface-app)", border: "1px solid var(--line)", borderRadius: 12, padding: 24, width: 580, maxHeight: "90vh", overflowY: "auto", display: "flex", flexDirection: "column", gap: 14 }}>
        <span style={{ fontSize: 15, fontWeight: 600, color: "var(--ink)" }}>{isEdit ? `Editar: ${initial.key}` : "Nova variável"}</span>

        {/* Key */}
        <div>
          <label style={{ fontSize: 12, color: "var(--ink-soft)", display: "block", marginBottom: 5 }}>Chave (snake_case)</label>
          <input style={inp} placeholder="ex: nome_empresa" value={form.key} onChange={(e) => setForm({ ...form, key: e.target.value })} disabled={isEdit} />
        </div>

        {/* Kind */}
        <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
          {(["static", "template", "webhook_path", "python"] as const).map((k) => (
            <label key={k} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, color: form.kind === k ? "var(--brand)" : "var(--ink-soft)", cursor: "pointer" }}>
              <input type="radio" checked={form.kind === k} onChange={() => setForm({ ...form, kind: k })} style={{ accentColor: "var(--brand)" }} />
              {kindLabel[k]}
            </label>
          ))}
        </div>

        {/* Value */}
        <div>
          <label style={{ fontSize: 12, color: "var(--ink-soft)", display: "block", marginBottom: 5 }}>
            {form.kind === "webhook_path" ? "Expressão JMESPath"
              : form.kind === "template" ? "Template ({{variavel}})"
              : form.kind === "python" ? "Código Python (atribua a result)"
              : "Valor"}
          </label>
          <textarea
            style={{ ...inp, height: form.kind === "python" ? 140 : 80, resize: "vertical", fontFamily: "var(--font-mono)" }}
            placeholder={form.kind === "webhook_path" ? "lead.profile_name"
              : form.kind === "template" ? "{{saudacao()}}, {{nome}}!"
              : form.kind === "python" ? "result = wh['lead']['profile_name'].split()[0]"
              : "valor fixo"}
            value={form.value}
            onChange={(e) => setForm({ ...form, value: e.target.value })}
          />
        </div>

        {/* Sample payload (webhook_path e python) */}
        {(form.kind === "webhook_path" || form.kind === "python") && (
          <div>
            <label style={{ fontSize: 12, color: "var(--ink-soft)", display: "block", marginBottom: 5 }}>Payload de exemplo (JSON)</label>
            <textarea
              style={{ ...inp, height: 100, fontFamily: "var(--font-mono)", fontSize: 12, resize: "vertical" }}
              placeholder='{"lead": {"profile_name": "Giovani", "phone": "5511999999999"}, "message": {"type": "text", "text": "oi"}}'
              value={sampleText}
              onChange={(e) => setSampleText(e.target.value)}
            />
          </div>
        )}

        {/* Testar */}
        {(form.kind === "python" || form.kind === "webhook_path" || form.kind === "template") && (
          <div>
            <button onClick={() => { setTestOut(null); test.mutate(); }} disabled={test.isPending}
              style={{ padding: "6px 12px", background: "var(--surface-input)", border: "1px solid var(--line-strong)", borderRadius: 8, color: "var(--ink-secondary)", fontSize: 12, cursor: "pointer" }}>
              {test.isPending ? "Executando..." : "▶ Executar"}
            </button>
            {testOut !== null && (
              <div style={{ marginTop: 8, padding: "8px 12px", background: "var(--surface-raised)", border: "1px solid var(--line)", borderRadius: 8, color: "#2F7D22", fontSize: 12, whiteSpace: "pre-wrap", wordBreak: "break-word", display: "flex", flexDirection: "column", gap: 4 }}>
                {testOut.output != null && (
                  <div style={{ display: "flex", gap: 6 }}><PrinterIcon size={13} /><span>{testOut.output}</span></div>
                )}
                {testOut.error != null ? (
                  <div style={{ display: "flex", gap: 6, color: "var(--danger)" }}><AlertTriangleIcon size={13} /><span>{testOut.error}</span></div>
                ) : (
                  <span>→ {testOut.result}</span>
                )}
              </div>
            )}
          </div>
        )}

        {/* Description */}
        <div>
          <label style={{ fontSize: 12, color: "var(--ink-soft)", display: "block", marginBottom: 5 }}>Descrição (opcional)</label>
          <input style={inp} placeholder="Para que serve essa variável..." value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
        </div>

        {error && <div style={{ fontSize: 12, color: "var(--danger)", background: "var(--danger-soft)", border: "1px solid var(--danger-soft-line)", borderRadius: 8, padding: "8px 12px" }}>{error}</div>}

        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
          <button onClick={onClose} style={{ padding: "8px 16px", background: "var(--surface-input)", border: "1px solid var(--line-strong)", borderRadius: 8, color: "var(--ink-secondary)", fontSize: 13, cursor: "pointer" }}>Cancelar</button>
          <button onClick={() => mut.mutate()} disabled={mut.isPending} style={{ padding: "8px 16px", background: "var(--brand)", border: "none", borderRadius: 8, color: "#fff", fontSize: 13, cursor: "pointer" }}>
            {mut.isPending ? "Salvando..." : "Salvar"}
          </button>
        </div>
      </DialogPanel>
    </div>
  );
}
